import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, Save, Loader2, Pill, Search, CheckCircle2, AlertTriangle, Users, Pencil, RefreshCw } from "lucide-react";
import { api, fmtDate } from "../lib/api";
import FormError from "./ui/FormError";
import useEscClose from "../hooks/useEscClose";
import BulkBatchesModal from "./BulkBatchesModal";

/**
 * BulkSupplementModal — squad-wide supplement prescription wizard.
 *
 * Coach fills in the supplement (name / dose / start_date / etc.) ONCE,
 * ticks whichever athletes need it, hits Save → backend applies the same
 * record to every selected athlete with a duplicate-guard (same name +
 * start_date already exists → skip).
 *
 * Companion to BulkDewormingModal; kept intentionally leaner (no urgency
 * chip fetch) because supplements don't have a schedule engine driving
 * per-athlete urgency.
 */
export default function BulkSupplementModal({ open, onClose, onSaved }) {
  const [sailors, setSailors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  // Supplement fields
  const [name, setName] = useState("");
  const [dose, setDose] = useState("");
  const [frequency, setFrequency] = useState("daily");
  const [frequencyCustom, setFrequencyCustom] = useState("");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState("");
  const [prescribedBy, setPrescribedBy] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");

  // Athlete picker
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [picked, setPicked] = useState(() => new Set());

  // Existing-batches lookup: mirror the deworming wizard so a coach who
  // picks a date that already has a bulk supplement batch is offered
  // "edit older data" instead of forcing a new one.
  const [allBatches, setAllBatches] = useState([]);
  const [showBatchesModal, setShowBatchesModal] = useState(null);

  useEscClose(open, () => { if (!busy) onClose?.(); });

  useEffect(() => {
    if (!open) return;
    setError(""); setResult(null); setPicked(new Set());
    setName(""); setDose(""); setFrequency("daily"); setFrequencyCustom("");
    setStartDate(new Date().toISOString().slice(0, 10)); setEndDate("");
    setPrescribedBy(""); setReason(""); setNotes("");
    setSearch(""); setGroupFilter("all");
    setLoading(true);
    api.get("/sailors")
      .then((r) => setSailors(r.data || []))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load athletes"))
      .finally(() => setLoading(false));
    api.get("/bulk-batches", { params: { kind: "supplement" } })
      .then((r) => setAllBatches(r.data?.items || []))
      .catch(() => setAllBatches([]));
  }, [open]);

  // Match batch on same start_date + name (case-insensitive). Only shown
  // once the coach has typed a name so we don't nag on the empty state.
  const existingBatchOnDate = useMemo(() => {
    if (!startDate || !name.trim() || allBatches.length === 0) return null;
    const n = name.trim().toLowerCase();
    return allBatches.find((b) =>
      b.date === startDate && (b.data?.name || "").toLowerCase() === n,
    ) || null;
  }, [startDate, name, allBatches]);

  const groups = useMemo(() => {
    const g = new Set();
    sailors.forEach((s) => { if (s.group) g.add(s.group); });
    return Array.from(g).sort();
  }, [sailors]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sailors.filter((s) => {
      if (groupFilter !== "all" && s.group !== groupFilter) return false;
      if (q && !s.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [sailors, search, groupFilter]);

  const togglePick = (id) => setPicked((prev) => {
    const s = new Set(prev);
    if (s.has(id)) s.delete(id); else s.add(id);
    return s;
  });
  const pickAllFiltered = () => setPicked(new Set(filtered.map((s) => s.id)));
  const clearPicked = () => setPicked(new Set());

  const canSubmit = name.trim() && startDate && picked.size > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setError("");
    try {
      const r = await api.post("/supplements/bulk", {
        name: name.trim(),
        dose: dose || null,
        frequency,
        frequency_custom: frequency === "custom" ? (frequencyCustom || null) : null,
        start_date: startDate,
        end_date: endDate || null,
        prescribed_by: prescribedBy || null,
        reason: reason || null,
        notes: notes || null,
        status: "active",
        sailor_ids: Array.from(picked),
      });
      setResult(r.data);
      onSaved?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto py-6"
      style={{ backdropFilter: "blur(6px)", background: "rgba(11,22,39,0.72)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="bulk-supplement-modal"
    >
      <div className="card w-full max-w-5xl mx-4 p-5 md:p-6 relative" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
              <Pill size={11} /> Squad supplement day
            </div>
            <div className="font-display text-lg text-slate-100 mt-0.5">Prescribe to multiple athletes</div>
            <div className="text-[11px] text-slate-400 mt-0.5">Fill the supplement once, tick everyone it applies to.</div>
          </div>
          <button onClick={onClose} className="pill !p-1.5" data-testid="bulk-supp-close"><X size={14} /></button>
        </div>

        {result ? (
          <BulkResult result={result} onAnother={() => setResult(null)} onClose={onClose} />
        ) : (
          <>
            {error && <div className="mb-3"><FormError message={error} onDismiss={() => setError("")} testId="bulk-supp-error" /></div>}

            {/* Supplement fields */}
            <div className="card !bg-[#0B1627] p-3 mb-3 grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="block md:col-span-2">
                <div className="text-[9px] uppercase tracking-widest text-slate-400">Supplement name *</div>
                <input type="text" maxLength={120} value={name} onChange={(e) => setName(e.target.value)}
                       placeholder="e.g. Vitamin D 60K IU"
                       className="form-input w-full" data-testid="bulk-supp-name" />
              </label>
              <label className="block">
                <div className="text-[9px] uppercase tracking-widest text-slate-400">Dose</div>
                <input type="text" maxLength={80} value={dose} onChange={(e) => setDose(e.target.value)}
                       placeholder="1 capsule, 5 ml"
                       className="form-input w-full" data-testid="bulk-supp-dose" />
              </label>
              <label className="block">
                <div className="text-[9px] uppercase tracking-widest text-slate-400">Frequency</div>
                <select value={frequency} onChange={(e) => setFrequency(e.target.value)}
                        className="form-input w-full" data-testid="bulk-supp-freq">
                  <option value="daily">Daily</option>
                  <option value="alternate">Alternate days</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="once">One-off</option>
                  <option value="custom">Custom…</option>
                </select>
              </label>
              {frequency === "custom" && (
                <label className="block md:col-span-2">
                  <div className="text-[9px] uppercase tracking-widest text-slate-400">Custom frequency</div>
                  <input type="text" maxLength={80} value={frequencyCustom}
                         onChange={(e) => setFrequencyCustom(e.target.value)}
                         placeholder="e.g. Every 3 days"
                         className="form-input w-full" data-testid="bulk-supp-freq-custom" />
                </label>
              )}
              <label className="block">
                <div className="text-[9px] uppercase tracking-widest text-slate-400">Start date *</div>
                <input type="date" required value={startDate}
                       onChange={(e) => setStartDate(e.target.value)}
                       className="form-input w-full font-mono-data" data-testid="bulk-supp-start" />
              </label>
              <label className="block">
                <div className="text-[9px] uppercase tracking-widest text-slate-400">End date</div>
                <input type="date" value={endDate}
                       onChange={(e) => setEndDate(e.target.value)}
                       className="form-input w-full font-mono-data" data-testid="bulk-supp-end" />
              </label>
              <label className="block">
                <div className="text-[9px] uppercase tracking-widest text-slate-400">Prescribed by</div>
                <input type="text" maxLength={120} value={prescribedBy}
                       onChange={(e) => setPrescribedBy(e.target.value)}
                       placeholder="Dr. ___"
                       className="form-input w-full" data-testid="bulk-supp-prescriber" />
              </label>
              <label className="block md:col-span-3">
                <div className="text-[9px] uppercase tracking-widest text-slate-400">Reason</div>
                <input type="text" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)}
                       placeholder="e.g. Iron deficiency across pre-season labs"
                       className="form-input w-full" data-testid="bulk-supp-reason" />
              </label>
            </div>

            {existingBatchOnDate && (
              <div className="mb-2 rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-3 py-2 text-[11px] text-cyan-100 flex items-start gap-2"
                   data-testid="bulk-supp-existing-batch">
                <RefreshCw size={12} className="mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="font-semibold">
                    A bulk supplement batch for &quot;{existingBatchOnDate.data?.name}&quot; already exists on {fmtDate(startDate)}
                  </div>
                  <div className="text-cyan-200/80 text-[10px] mt-0.5">
                    {existingBatchOnDate.athlete_count} athlete{existingBatchOnDate.athlete_count === 1 ? "" : "s"} already on it. Edit older data or add fresh?
                  </div>
                </div>
                <button onClick={() => setShowBatchesModal(existingBatchOnDate.batch_id)}
                        className="pill !text-[10px] !py-1 !px-2 active"
                        data-testid="bulk-supp-edit-existing">
                  <Pencil size={10} /> Edit older data
                </button>
              </div>
            )}

            {/* Athlete picker */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <div className="relative">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input type="text" placeholder="Search athlete…" value={search}
                       onChange={(e) => setSearch(e.target.value)}
                       className="pl-7 pr-3 py-1.5 bg-[#0E1A2E] border border-[#1E304B] rounded-lg text-sm placeholder-slate-500 focus:outline-none focus:border-[#00E5FF]/60"
                       data-testid="bulk-supp-search" />
              </div>
              <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}
                      className="py-1.5 px-2 bg-[#0E1A2E] border border-[#1E304B] rounded-lg text-[11px] text-slate-200"
                      data-testid="bulk-supp-group">
                <option value="all">All squads</option>
                {groups.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
              <button onClick={pickAllFiltered} className="pill !py-1 text-[11px]" data-testid="bulk-supp-pick-all">Select all shown</button>
              <button onClick={clearPicked} className="pill !py-1 text-[11px]" disabled={picked.size === 0} data-testid="bulk-supp-clear">Clear</button>
              <div className="ml-auto text-[11px] text-cyan-300 font-mono-data flex items-center gap-1">
                <Users size={11} /> {picked.size} picked
              </div>
            </div>

            <div className="rounded-lg border border-[#1E304B] max-h-[38vh] overflow-y-auto">
              {loading ? (
                <div className="py-6 text-center text-slate-400 text-[12px]"><Loader2 className="animate-spin inline" size={12} /> Loading…</div>
              ) : filtered.length === 0 ? (
                <div className="py-6 text-center text-slate-500 text-[11px]">No athletes match.</div>
              ) : (
                <ul>
                  {filtered.map((s) => {
                    const on = picked.has(s.id);
                    return (
                      <li key={s.id}
                          className={`px-3 py-1.5 border-b border-[#1E304B] last:border-b-0 flex items-center gap-3 cursor-pointer hover:bg-[#0F1E33] ${on ? "bg-cyan-500/5" : ""}`}
                          onClick={() => togglePick(s.id)}
                          data-testid={`bulk-supp-row-${s.id}`}>
                        <input type="checkbox" checked={on} readOnly className="accent-cyan-400" />
                        <div className="flex-1 text-[12px] text-slate-100 font-mono-data">{s.name}</div>
                        <div className="text-[10px] text-slate-500">{s.group}{s.subgroup ? ` · ${s.subgroup}` : ""}</div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              <button onClick={onClose} className="pill" disabled={busy}>Cancel</button>
              <button onClick={submit} disabled={!canSubmit || busy}
                      className="pill active disabled:opacity-40" data-testid="bulk-supp-save">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <><Save size={12} /> Prescribe to {picked.size}</>}
              </button>
            </div>
          </>
        )}
      </div>
      <BulkBatchesModal
        open={showBatchesModal != null}
        onClose={() => setShowBatchesModal(null)}
        focusBatchId={showBatchesModal}
      />
    </div>,
    document.body,
  );
}

function BulkResult({ result, onAnother, onClose }) {
  const c = result.counts;
  return (
    <div className="text-center py-6">
      <div className="rounded-full p-3 bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 inline-block mb-3">
        <CheckCircle2 size={30} />
      </div>
      <div className="font-display text-lg text-slate-100 mb-1">Prescribed to {c.created} athlete{c.created === 1 ? "" : "s"}</div>
      <div className="text-[11px] text-slate-400 mb-4">
        {c.skipped > 0 && <span>· {c.skipped} skipped (already prescribed)</span>}
        {c.not_found > 0 && <span className="text-rose-300 ml-2">· {c.not_found} not found</span>}
      </div>
      {(result.skipped || []).length > 0 && (
        <div className="text-[10px] text-amber-300 mb-3 flex items-start gap-1 justify-center">
          <AlertTriangle size={10} className="mt-0.5" />
          <div className="text-left">
            {(result.skipped || []).slice(0, 5).map((s) => (
              <div key={s.sailor_id}>{s.sailor_name}: {s.reason}</div>
            ))}
            {(result.skipped || []).length > 5 && <div>… and {result.skipped.length - 5} more</div>}
          </div>
        </div>
      )}
      <div className="flex items-center justify-center gap-2">
        <button onClick={onAnother} className="pill" data-testid="bulk-supp-another">Prescribe another</button>
        <button onClick={onClose} className="pill active" data-testid="bulk-supp-done">Done</button>
      </div>
    </div>
  );
}
